import { useIsFetching, useMutation } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import ClipsListItemWithActions from './ClipsListItemWithActions';
import MultiSelectFooter from './MultiSelectFooter';
import ClipListPreviewSkeleton from './ClipListPreviewSkeleton';
import CreateClipsPopover from './CreateClipsPopover';
import ClipDeletionErrorDialog, { ClipDeletionError } from './ClipDeletionErrorDialog';
import ClipBulkDeleteUsageDialog from './ClipBulkDeleteUsageDialog';
import ClipBulkDownloadDialog from './ClipBulkDownloadDialog';
import useMultiSelect from './useMultiSelect';
import useBulkDelete from './useBulkDelete';
import useBulkDownload from './useBulkDownload';
import CreateAISearchClipsButton from './CreateAISearchClipsButton';
import { NavigationEndpoint } from '../SessionDetail/types';
import { clipEditorEndpoint } from '@/utils/clipEditorRoutes';
import { deleteContentClip } from '@/libs/clipContentUtil';
import { classnames } from '@/libs/utils';
import useElementScrolledHook from '@/hooks/useElementScrolledHook';
import IconButton from '@/components/atoms/Button/IconButton';
import { clipGeneratingCount, isSimplifiedUploadEnabled } from '@/stores/clip';
import AssetRatingDialog from '@/components/molecules/AssetRating/AssetRatingDialog';
import useSavedSearch from '@/hooks/useSavedSearch';
import { core } from '@/stores/core';
import { useAppContext } from '@/context/AppContext/AppContext';
import useDialog from '@/components/organisms/useDialog';
import ClipUsageDialog from '@/components/molecules/ClipPublish/ClipUsageDialog';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useAssetUsage from '@/hooks/useAssetUsage';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import Icon from '@/components/atoms/Icon';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import { useUploadEditorReadinessContext } from '@/context/UploadEditorReadinessContext/UploadEditorReadinessContext';

export default function ClipsList({
  clips,
  canGenerate,
  isCollapsed,
  isHidden,
  generateClips,
  onDelete,
  onRenameClip,
  onGenerateSocialPost,
  onDuplicateClip,
  toggleCollapse,
  hideCollapseButton = false,
  animateClipList = true,
  isInPopover = false
}: {
  clips: Clip[];
  canGenerate: boolean;
  isCollapsed: boolean;
  isHidden: boolean;
  generateClips: () => Promise<void>;
  onDelete: (clipId: string, skipNavigation?: boolean) => void;
  onRenameClip: (clip: Clip) => void;
  onGenerateSocialPost: (clipId: string) => void;
  onDuplicateClip: (clipId: string) => void;
  toggleCollapse: () => void;
  hideCollapseButton?: boolean;
  animateClipList?: boolean;
  isInPopover?: boolean;
}) {
  const { eventId, broadcastId, id: savedSearchId } = useParams<{ eventId: string; broadcastId: string; id: string }>();
  const isFetching = useIsFetching({ queryKey: ['clips', broadcastId] });
  const { isScrolled, handleOnScroll } = useElementScrolledHook();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeClipId = useMemo(() => searchParams.get('activeClipId'), [searchParams]);
  const generatingCount = useSyncExternalStore(clipGeneratingCount.subscribe, clipGeneratingCount.getSnapshot);
  const [clipDeletionError, setClipDeletionError] = useState<ClipDeletionError>(null);
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const { adminAppStore } = useAppContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isMultiSelectEnabled = featureFlags[FeatureFlagKeys.Use_CL_Multi_Select_Assets];
  const { clipsReady } = useUploadEditorReadinessContext();

  const { isMultiSelectMode, selectedClipIds, enterMultiSelectMode, exitMultiSelectMode, toggleClipSelection } =
    useMultiSelect();

  const handleBulkDeleteSuccess = useCallback(
    (deletedIds: Set<string>) => {
      if (!activeClipId || !deletedIds.has(activeClipId)) {
        return;
      }
      const remainingClips = clips.filter(clip => !deletedIds.has(clip.id));
      if (remainingClips.length > 0) {
        setSearchParams({ activeClipId: remainingClips[0].id }, { replace: true });
      } else {
        navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.recordings}`, { replace: true });
      }
    },
    [activeClipId, clips, setSearchParams, navigate, eventId, broadcastId]
  );

  const {
    isBulkDeleting,
    isBulkConfirmDeleteDialogOpen,
    openBulkConfirmDeleteDialog,
    closeBulkConfirmDeleteDialog,
    isBulkDeleteUsageDialogOpen,
    closeBulkDeleteUsageDialog,
    bulkDeleteInUseUsages,
    onBulkDeleteClips
  } = useBulkDelete({
    selectedClipIds,
    exitMultiSelectMode,
    onDelete,
    setClipDeletionError,
    onBulkDeleteSuccess: handleBulkDeleteSuccess
  });

  const {
    onBulkDownloadClips,
    isBulkDownloading,
    isBulkDownloadDialogOpen,
    closeBulkDownloadDialog,
    onConfirmBulkDownload,
    downloadableClips,
    nonDownloadableClips
  } = useBulkDownload({ clips, selectedClipIds, exitMultiSelectMode });

  const closePopover = useCallback(() => {
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
  }, []);

  const { isSavedSearchPage } = useSavedSearch();
  const { usageData: clipAssetUsage, checkAssetUsage } = useAssetUsage();

  const {
    isOpen: isClipUsageDialogOpen,
    openDialog: openClipUsageDialog,
    closeDialog: closeClipUsageDialog
  } = useDialog();

  const {
    isOpen: isConfirmDeleteDialogOpen,
    openDialog: openConfirmDeleteDialog,
    closeDialog: closeConfirmDeleteDialog
  } = useDialog();

  const isVideoLibraryClipPublishEnabled = useMemo(
    () => adminAppStore.getters?.['org/getCurrentOrg']?.showVideoLibraryClipPublish || false,
    [adminAppStore]
  );

  const { mutate: deleteClip } = useMutation({
    mutationFn: (clipId: string) => deleteContentClip(clipId),
    onSuccess: (_, clipId) => {
      onDelete(clipId);
    },
    onError: (err: any, clipId) => {
      try {
        const parseError = JSON.parse(err);
        setClipDeletionError({
          clipId,
          message: parseError?.message,
          textAssets: parseError?.text_assets
        });
      } catch (error) {
        console.error('Failed to parse error:', err);
      }
    },
    onSettled: () => {
      closeConfirmDeleteDialog();
    }
  });

  const onDeleteClip = useCallback(() => {
    if (!selectedClip) {
      return;
    }
    deleteClip(selectedClip.id);
  }, [selectedClip, deleteClip]);

  const triggerClipDelete = useCallback(
    async (clipId: string, clip: Clip) => {
      // check clip usage before deletion
      if (isVideoLibraryClipPublishEnabled && clip?.asset_metadata?.video_library_id) {
        const usageCount = await checkAssetUsage(clip?.asset_metadata?.video_library_id);
        if (usageCount > 0) {
          openClipUsageDialog();
          return;
        }
      }
      // show confirm dialog for deletion
      if (isVideoLibraryClipPublishEnabled) {
        setSelectedClip(clip);
        openConfirmDeleteDialog();
        return;
      }

      deleteContentClip(clipId)
        .then(() => {
          onDelete(clipId);
        })
        .catch((err: any) => {
          const parseError = JSON.parse(err);
          setClipDeletionError({ clipId, message: parseError?.message, textAssets: parseError?.text_assets });
        });
    },
    [onDelete, isVideoLibraryClipPublishEnabled, checkAssetUsage, openClipUsageDialog, openConfirmDeleteDialog]
  );

  const onCloseClipDeletionErrorDialog = useCallback(() => {
    setClipDeletionError(null);
  }, []);

  const isGeneratingClips = useMemo(() => {
    return !!generatingCount || (!!isFetching && !clips.length);
  }, [clips.length, generatingCount, isFetching]);

  const onClipSelect = useCallback(
    (clip: Clip) => {
      // Reels open at /highlights and clips at /clips, so picking one from the other's list moves
      // route as well as clip. Saved search has no /highlights route — everything opens at /clips.
      const endpoint = isSavedSearchPage
        ? NavigationEndpoint.clips
        : clipEditorEndpoint(clip.asset_metadata?.clip_type);

      if (!pathname.endsWith(`/${endpoint}`)) {
        if (isSavedSearchPage) {
          navigate(`/search/${savedSearchId}/${endpoint}`);
        } else {
          navigate(`/${eventId}/${broadcastId}/${endpoint}`);
        }
      }
      setSearchParams({ activeClipId: clip.id });
      if (coreStore.content?.id !== clip.content.id) {
        core.set(() => ({ content: clip.content }));
      }
      closePopover();
    },
    [
      setSearchParams,
      coreStore.content,
      pathname,
      eventId,
      broadcastId,
      navigate,
      closePopover,
      isSavedSearchPage,
      savedSearchId
    ]
  );

  const closeClipListPopover = useCallback(() => {
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
  }, []);

  return (
    <>
      {!isHidden && (
        <div className="absolute bottom-0 h-[69vh] w-full bg-gradient-to-t from-slate-950/[0.13] to-slate-950/0 blur"></div>
      )}
      <div
        className={classnames(
          'z-20 flex w-full shrink-0 flex-col items-stretch overflow-hidden rounded-2xl border !border-slate-100 bg-white shadow-xl shadow-slate-200/10 transition-all duration-[400ms]',
          { hidden: isHidden }
        )}
      >
        <div className="flex h-full flex-col">
          <div
            className={classnames(
              'sticky top-0 z-20 flex h-14 w-full items-center justify-between bg-white/90 p-4 px-5 backdrop-blur transition-all duration-150 ease-in-out',
              {
                'shadow-lg shadow-slate-900/[0.075]': isScrolled
              }
            )}
          >
            <div className="flex items-center space-x-2">
              {isMultiSelectMode ? (
                <>
                  <span className="text-base font-medium">Multi-select clips:</span>
                  <span className="inline-flex rounded-full bg-slate-200 px-3 py-1 text-xs font-medium">
                    {selectedClipIds.size}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg font-medium">Clips</span>
                  {!isFetching && (
                    <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium">
                      {clips.length}
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex space-x-1.5">
              {isMultiSelectEnabled && clips.length > 0 && (
                <IconButton
                  icon={'IconListCheck'}
                  title={isMultiSelectMode ? 'Exit multi-select' : 'Multi-select clips'}
                  variation="text"
                  buttonClassName={isMultiSelectMode ? '!bg-slate-100 !px-3.5 !py-1.5' : ''}
                  trackingId="clips-list-multiselect-button"
                  onClick={isMultiSelectMode ? exitMultiSelectMode : enterMultiSelectMode}
                />
              )}
              {!hideCollapseButton && (
                <IconButton
                  icon={isCollapsed ? 'IconArrowBarRight' : 'IconArrowBarLeft'}
                  title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                  variation="text"
                  trackingId="clips-list-collapse-button"
                  onClick={toggleCollapse}
                />
              )}
              {isInPopover && (
                <IconButton
                  icon={'IconX'}
                  title={'Close'}
                  variation="text"
                  trackingId="clips-list-cancel-button"
                  onClick={closeClipListPopover}
                />
              )}
            </div>
          </div>
          <div className="flex grow flex-col overflow-y-auto overflow-x-hidden" onScroll={handleOnScroll}>
            {isSimplifiedUploadEnabled() && !clipsReady && !!clips.length && (
              <div className="m-3 shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Icon name="IconLoader2" className="size-4 shrink-0 animate-spin text-slate-700" />
                  <span className="text-sm font-semibold text-slate-800">Curating AI Clips</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">Curating AI clips as the video is being processed.</p>
              </div>
            )}
            {!isFetching && !generatingCount && !clips.length ? (
              isSimplifiedUploadEnabled() && !clipsReady ? (
                <div className="flex size-full grow flex-col items-center justify-center gap-3">
                  <Icon name="IconLoader2" className="size-6 animate-spin text-slate-400" />
                  <div className="text-center">
                    <div className="text-sm font-semibold text-slate-600">Curating AI Clips</div>
                    <div className="mt-1 text-xs text-slate-400">
                      AI Clips will be created after the video is processed.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex size-full grow items-center justify-center">
                  <div className="">
                    <div className="flex w-full justify-center">
                      <Icon name="IconMovie" className="size-7 text-slate-400" />
                    </div>
                    <div className="mt-2.5 px-4 text-center text-sm text-slate-400">
                      <div className="font-medium">No clips, yet.</div>
                      {!isSavedSearchPage && (
                        <div className="mt-1 leading-tight">Highlight the transcript or generate automagically.</div>
                      )}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <></>
            )}
            {!isSavedSearchPage && isGeneratingClips ? (
              <ClipListPreviewSkeleton
                count={generatingCount || 5}
                isCollapsed={isCollapsed}
                isInPopover={isInPopover}
              />
            ) : (
              <></>
            )}
            {!isFetching && clips.length ? (
              clips.map((clip: Clip) => (
                <Transition
                  key={clip.asset_metadata.id}
                  show={!clip.isRemoved}
                  leave="transition transform duration-500 delay-100 ease-out"
                  leaveFrom="transform translate-x-0"
                  leaveTo="transform translate-x-[300px]"
                >
                  <ClipsListItemWithActions
                    clip={clip}
                    animateClipList={animateClipList}
                    isCollapsed={isCollapsed}
                    onDelete={clipId => triggerClipDelete(clipId, clip)}
                    onRenameClip={onRenameClip}
                    onGenerateSocialPost={onGenerateSocialPost}
                    onClipSelect={onClipSelect}
                    onDuplicateClip={onDuplicateClip}
                    isInPopover={isInPopover}
                    isMultiSelectMode={isMultiSelectMode}
                    isSelected={selectedClipIds.has(clip.id)}
                    isSelectionLimitReached={selectedClipIds.size >= 10}
                    onToggleSelection={toggleClipSelection}
                  />
                </Transition>
              ))
            ) : (
              <></>
            )}
            {isSavedSearchPage && isGeneratingClips ? (
              <ClipListPreviewSkeleton
                count={generatingCount || 5}
                isCollapsed={isCollapsed}
                isInPopover={isInPopover}
              />
            ) : (
              <></>
            )}
          </div>
          <div className="border-t bg-white px-4 py-3">
            {isMultiSelectMode ? (
              <MultiSelectFooter
                selectedCount={selectedClipIds.size}
                onCancel={exitMultiSelectMode}
                onDelete={openBulkConfirmDeleteDialog}
                onDownload={onBulkDownloadClips}
                isDownloadDisabled={downloadableClips.length === 0}
              />
            ) : isSavedSearchPage ? (
              <CreateAISearchClipsButton
                generateClips={generateClips}
                buttonTextContent={!clips.length ? 'Generate Clips' : 'Generate More'}
              />
            ) : (
              <CreateClipsPopover generateClips={generateClips} canGenerate={canGenerate} />
            )}
          </div>
        </div>
      </div>
      <ClipDeletionErrorDialog
        isOpen={!!clipDeletionError}
        onClose={onCloseClipDeletionErrorDialog}
        error={clipDeletionError}
      />
      <AssetRatingDialog />
      <ContentDialog
        isOpen={isClipUsageDialogOpen}
        hideCloseIcon={true}
        size="medium"
        setIsOpen={closeClipUsageDialog}
        disableBackdropClose={false}
      >
        <ClipUsageDialog onClose={closeClipUsageDialog} clipAssetUsage={clipAssetUsage} type={'DELETE'} />
      </ContentDialog>
      <ConfirmationDialog
        isOpen={isConfirmDeleteDialogOpen}
        onClose={closeConfirmDeleteDialog}
        onConfirm={onDeleteClip}
        title="Delete Clip"
        content="Are you sure you want to delete this clip? This will permanently delete the clip and you will not be able to revert this change."
        confirmLabel="Yes"
        cancelLabel="Cancel"
      />
      <ConfirmationDialog
        isOpen={isBulkConfirmDeleteDialogOpen}
        onClose={closeBulkConfirmDeleteDialog}
        onConfirm={onBulkDeleteClips}
        title="Delete Clips"
        content={`Are you sure you want to delete ${selectedClipIds.size} clip(s)? This will permanently delete them and cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmLoading={isBulkDeleting}
      />
      <ClipBulkDeleteUsageDialog
        isOpen={isBulkDeleteUsageDialogOpen}
        onClose={closeBulkDeleteUsageDialog}
        usages={bulkDeleteInUseUsages}
      />
      <ClipBulkDownloadDialog
        isOpen={isBulkDownloadDialogOpen}
        onClose={closeBulkDownloadDialog}
        onConfirm={onConfirmBulkDownload}
        downloadableClips={downloadableClips}
        nonDownloadableClips={nonDownloadableClips}
        isLoading={isBulkDownloading}
      />
    </>
  );
}
